/**
 * Pure CSV serialisation helpers.
 * No React and no domain dependencies live here.
 */

/**
 * Escapes a single cell value for RFC 4180–compliant CSV:
 * wraps in double quotes when the value contains a comma, a double quote,
 * or a newline; doubles any embedded double quotes.
 */
function escapeCell(value: unknown): string {
  const text = value == null ? '' : String(value);
  const needs_quoting = text.includes(',') || text.includes('"') || text.includes('\n') || text.includes('\r');
  if (needs_quoting) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

/**
 * Serialises an array of flat objects to a CSV string.
 * Column order follows the key order of the first row.
 * Returns an empty string when the array is empty.
 */
export function serialiseToCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return '';

  const headers = Object.keys(rows[0]);

  const lines = [
    headers.map((header) => escapeCell(header)).join(','),
    ...rows.map((row) => headers.map((header) => escapeCell(row[header])).join(',')),
  ];

  return lines.join('\n');
}

/**
 * Triggers a browser file download for the given CSV string.
 * Creates a temporary anchor element, clicks it, then revokes the object URL.
 */
export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.setAttribute('href', url);
  anchor.setAttribute('download', filename);
  anchor.click();

  URL.revokeObjectURL(url);
}
